import React, { useState } from 'react';
import { useFormik } from 'formik';
import { TextField, Box, Typography } from '@mui/material';
import { guestOrderValidationSchema } from '../validations/orderValidations';
import { OrderData as OrderDataType, TestKitDataType } from '../types/orderTypes';
import ActionButton from '../components/common/buttons/ActionButton';
import OrderDetailsContent from './OrderDetailsContent';
import useBackendApi from '../hooks/useBackendApi';
import { useParams, useNavigate } from 'react-router-dom';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import { HeaderOptions, RequestOptions } from '../types/httpRequests';

const backendApiUrl = process.env.REACT_APP_BACKEND_URL;

const GuestOrderDetails: React.FC = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<OrderDataType | null>(null);
  const [testKits, setTestKits] = useState<TestKitDataType[]>([]);
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const { isLoading, apiFetchRequest } = useBackendApi();
  const { renderSnackBarAlertComponent } = useSnackBarAlertMessages({ autoHideDuration: 10000 });
  const shopifyAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;

  const handleSubmit = async (values: { name: string; email: string }) => {
    const encodedName = encodeURIComponent(values.name);
    const encodedEmail = encodeURIComponent(values.email);
    const guestOrderUrl = `${backendApiUrl}/guest-orders/${shopifyOrderId}?name=${encodedName}&email=${encodedEmail}`;
    const guestTestKitsOrderUrl = `${backendApiUrl}/guest-orders/${shopifyOrderId}/test_kits?name=${encodedName}&email=${encodedEmail}`;
    let headers: HeaderOptions = {
      'x-shop-key': shopifyAccessToken,
    };

    const apiRequestOptions: RequestOptions = {
      endpoint: guestOrderUrl,
      headers: headers,
    };

    const getTestKitsOrderOptions: RequestOptions = {
      endpoint: guestTestKitsOrderUrl,
      headers: headers,
    };

    try {
      const [orderData, testKitsData] = await Promise.all([
        apiFetchRequest(apiRequestOptions),
        apiFetchRequest(getTestKitsOrderOptions),
      ]);

      if (orderData.status !== 200) {
        return navigate('/404.html');
      }

      setOrderData(orderData.body);
      return setTestKits(testKitsData.body?.test_kits || []);
    } catch (err) {
      console.log(err, 'err');
    }
  };

  const formik = useFormik({
    initialValues: { name: '', email: '' },
    validationSchema: guestOrderValidationSchema,
    onSubmit: handleSubmit,
  });

  const handleButtonClick = () => {
    formik.handleSubmit();
  };

  const renderButtonOrLoading = () => {
    if (!isLoading) {
      return <ActionButton label="Find order details" customStyles={{ width: 'auto' }} onClick={handleButtonClick} />;
    }

    return <GradientCircularProgressContainer loadingMessage="Searching order..." />;
  };

  const renderSearchForm = () => {
    if (orderData) return null;

    return (
      <Box sx={{ p: 2, maxWidth: 600, margin: 'auto' }}>
        <Typography variant="h5" gutterBottom>
          Validate order information
        </Typography>
        <Typography variant="body1" gutterBottom>
          To view your order details, please provider the following information to verify that you are the owner of this
          order.
        </Typography>
        <Box component="form" noValidate autoComplete="off">
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            {...formik.getFieldProps('name')}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            {...formik.getFieldProps('email')}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          {renderButtonOrLoading()}
        </Box>
      </Box>
    );
  };

  const renderOrderDetails = () => {
    if (!orderData) return null;

    return <OrderDetailsContent orderData={orderData} testKitsData={testKits} />;
  };

  return (
    <>
      {renderSearchForm()}
      {renderOrderDetails()}
      {renderSnackBarAlertComponent()}
    </>
  );
};

export default GuestOrderDetails;
